import { useEffect, useState } from "react";
import { Button, Card, Cluster, LabelWithHint, Stack, useToast } from "@salesapp/ui";
import { ApiError } from "../lib/api";
import { loxleyApi, type GuardrailOp, type GuardrailRules, type LoxleyGuardrails } from "../lib/loxleyApi";
import "./loxley.css";

const OP_LABELS: Record<GuardrailOp, string> = {
  read: "Read",
  create: "Create",
  update: "Update",
  delete: "Delete",
  send: "Email customers",
};
const OP_TIPS: Record<GuardrailOp, string> = {
  read: "Loxley may look up these records to answer a question. It cannot change them from a read.",
  create: "Loxley may prepare a new record. The person still confirms it before it is saved.",
  update: "Loxley may prepare a change to an existing record. The person still confirms it before it is saved.",
  delete: "Loxley may propose a delete. The delete can only be approved in Saleslox.",
  send: "Loxley may prepare an email to a customer. Sending it can only be approved in Saleslox.",
};
const COLUMNS: GuardrailOp[] = ["read", "create", "update", "delete", "send"];

function allowed(rules: GuardrailRules, tab: string, op: GuardrailOp): boolean {
  return rules[tab]?.[op] !== false;
}

/**
 * Organization settings, AI tab: what Loxley may do, per tab. An unticked box is a hard
 * stop on the server (the tool is hidden, refused, and re-checked at confirm), on top of
 * each person's own permissions. Only boxes some Loxley tool uses are shown.
 */
export function LoxleyGuardrailsPanel({ orgId }: { orgId: string }) {
  const toast = useToast();
  const [data, setData] = useState<LoxleyGuardrails | null>(null);
  const [rules, setRules] = useState<GuardrailRules>({});
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loxleyApi
      .guardrails(orgId)
      .then((value) => { setData(value); setRules(value.rules); })
      .catch(() => setFailed(true));
  }, [orgId]);

  function toggle(tab: string, op: GuardrailOp, on: boolean) {
    setRules((current) => {
      const ops = { ...(current[tab] ?? {}) };
      if (on) delete ops[op];
      else ops[op] = false;
      const next = { ...current };
      if (Object.keys(ops).length) next[tab] = ops;
      else delete next[tab];
      return next;
    });
  }

  function setAll(on: boolean) {
    if (!data) return;
    if (on) {
      setRules({});
      return;
    }
    setRules(Object.fromEntries(data.tabs.map((tab) => [tab.key, Object.fromEntries(tab.ops.map((op) => [op, false]))])));
  }

  async function save() {
    setSaving(true);
    try {
      const saved = await loxleyApi.saveGuardrails(orgId, rules);
      setData(saved);
      setRules(saved.rules);
      toast.show("Loxley guardrails saved. They apply from the next request.", "success");
    } catch (error) {
      toast.show(error instanceof ApiError ? error.message : "The guardrails could not be saved.", "danger");
    } finally {
      setSaving(false);
    }
  }

  if (failed) return null;
  if (!data) return null;
  const dirty = JSON.stringify(rules) !== JSON.stringify(data.rules);

  return (
    <Card>
      <Stack gap={3}>
        <div>
          <h2 className="home-section-title">Loxley guardrails</h2>
          <p className="home-subtitle">
            Tick what Loxley may do for people in your organization. Anything left unticked is blocked for every
            person, in the app and from connected AI apps, whatever their own role allows. Loxley still needs each
            person&rsquo;s own permission for anything ticked, and still asks before it changes anything.
          </p>
          {!data.enabled && (
            <p className="crm-muted" role="status">
              Loxley is not on your plan yet. These rules are saved now and apply as soon as it is.
            </p>
          )}
        </div>
        <div className="loxley-guardrails-scroll">
          <table className="loxley-table loxley-guardrails">
            <thead>
              <tr>
                <th>Area</th>
                {COLUMNS.map((op) => (
                  <th key={op}>
                    <LabelWithHint label={OP_LABELS[op]} tooltip={OP_TIPS[op]} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.tabs.map((tab) => (
                <tr key={tab.key}>
                  <td>{tab.label}</td>
                  {COLUMNS.map((op) => (
                    <td key={op}>
                      {tab.ops.includes(op) ? (
                        <input
                          type="checkbox"
                          aria-label={`${tab.label}: ${OP_LABELS[op]}`}
                          checked={allowed(rules, tab.key, op)}
                          onChange={(event) => toggle(tab.key, op, event.target.checked)}
                        />
                      ) : (
                        <span className="crm-muted" aria-hidden="true">–</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Cluster gap={3} align="center">
          <Button onClick={() => void save()} disabled={saving || !dirty}>
            {saving ? "Saving…" : "Save guardrails"}
          </Button>
          <Button variant="secondary" onClick={() => setAll(true)} disabled={saving}>Allow all</Button>
          <Button variant="ghost" onClick={() => setAll(false)} disabled={saving}>Block all</Button>
        </Cluster>
      </Stack>
    </Card>
  );
}
